import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { getStored, setStored, removeStored } from "@/lib/storage";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [checking, setChecking] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      removeStored("woc_token");
      setUser(false);
    } finally {
      setChecking(false);
    }
  }, []);

  // Handle Emergent Google auth callback (session_id in URL fragment) before anything else.
  const exchangeSession = useCallback(async (sessionId) => {
    try {
      const { data } = await api.post("/auth/session", {}, { headers: { "X-Session-ID": sessionId } });
      setUser(data.user);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Google sign-in failed.";
      toast.error(msg);
      setUser(false);
    } finally {
      setChecking(false);
      // Strip the fragment so the session_id is used once and discarded.
      const clean = window.location.pathname + window.location.search;
      window.history.replaceState({}, document.title, clean);
    }
  }, []);

  useEffect(() => {
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (match) {
      exchangeSession(decodeURIComponent(match[1]));
      return;
    }
    loadMe();
  }, [loadMe, exchangeSession]);

  // Password login (existing admin)
  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setStored("woc_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    removeStored("woc_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
